import { useMemo, useState, useEffect, ChangeEvent } from 'react';
import { Buffer } from 'buffer';
import FileSaver from 'file-saver';
import {
    Box,
    Button,
    Center,
    Checkbox,
    Flex,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Spinner,
    Text,
    Tooltip,
} from '@chakra-ui/react';
import { SortByFn, Column, useFilters, useGlobalFilter, usePagination, useSortBy, useTable } from 'react-table';
import { useGetRetailerByClientQuery } from 'core/API/authApi';
import { useGetCompanyInfoSelector } from 'core/redux/selectors/authSelectors';
import { caseInsensitiveStringsOrder } from 'plugins/DITable/helpers/sort';
import { useTranslation } from 'helpers/languages/translations';
import DefaultColumnFilter from 'plugins/DITable/filters/DefaultColumnFilter';
import { useGetSalesDataQuery, useLazyDownloadSalesDataQuery } from 'core/API/SalesDataApi';
import { TSalesData } from 'pages/SalesData/types';
import { ChevronDownIcon } from '@chakra-ui/icons';
import icons from 'shared/icons';

const useSalesData = () => {
    const [languages, setLanguages] = useState<string[]>([]);
    const [filters, setFilters] = useState<{ id: string; value: string }[]>([]);
    const { data: retailers, isLoading: isRetailersLoading, isError: isRetailerError } = useGetRetailerByClientQuery();
    const translate = useTranslation();
    const [selectedRows, setSelectedRows] = useState<string[]>([]);
    const { data: salesData, isError: isSalesDataError, isLoading: isSalesDataLoading } = useGetSalesDataQuery();
    const companyInfo = useGetCompanyInfoSelector();
    const [downloadFile] = useLazyDownloadSalesDataQuery();
    const [filesDownloading, setFilesDownloading] = useState<string[]>([]);

    useEffect(() => {
        if (!isRetailersLoading && !isRetailerError && retailers) {
            const langs: Set<string> = new Set<string>();
            retailers.data.forEach((retailer) => {
                if (retailer.languages) {
                    retailer.languages.forEach((l: string) => {
                        if (l !== '') langs.add(l);
                    });
                }
            });

            const langsArr = Array.from(langs);
            setLanguages(langsArr);
        }
    }, [retailers, isRetailersLoading]);

    const data: Readonly<TSalesData[]> = useMemo(() => {
        if (!isSalesDataLoading && !isSalesDataError && salesData) {
            return salesData;
        }
        return [] as TSalesData[];
    }, [isSalesDataLoading, isSalesDataError, salesData]);

    const caseInsensitiveSort = useMemo(() => caseInsensitiveStringsOrder, []);
    const sortTypes: Record<string, SortByFn<any>> = useMemo(
        () => ({
            caseInsensitive: caseInsensitiveSort,
        }),
        [],
    );

    const defaultColumn = useMemo(
        () => ({
            Filter: DefaultColumnFilter,
        }),
        [],
    );

    const handleCheckAllRows = () => {
        if (salesData) {
            if (selectedRows.length < salesData.length) setSelectedRows(salesData.map((k) => k._id));
            else setSelectedRows([]);
        }
    };

    const handleCheckRow = (event: ChangeEvent<HTMLInputElement>, id: string) => {
        if (event.target.checked) setSelectedRows([...selectedRows, id]);
        else setSelectedRows(selectedRows.filter((e) => e !== id));
    };

    const columns: Readonly<Column[]> = useMemo(() => {
        const nbLines = salesData ? salesData.length : 0;

        const tableColumns: readonly (Column | null)[] = [
            {
                Header: () => (
                    <Center m={0}>
                        <Checkbox
                            onChange={handleCheckAllRows}
                            isIndeterminate={selectedRows.length > 0 && selectedRows.length < nbLines}
                            isChecked={nbLines > 0 && selectedRows.length === nbLines}
                        ></Checkbox>
                    </Center>
                ),
                id: 'selector',
                disableFilters: true,
                Cell: ({
                    cell: {
                        row: { original },
                    },
                }) => {
                    const cellValue = original as TSalesData;
                    return (
                        <Center>
                            <Checkbox
                                onChange={(e) => {
                                    handleCheckRow(e, cellValue._id);
                                }}
                                isChecked={selectedRows.includes(cellValue._id)}
                            ></Checkbox>
                        </Center>
                    );
                },
                width: '5%',
            },

            {
                Header: translate('sales_data.column.name') as string,
                accessor: 'name',
                sortType: 'caseInsensitive',
                width: '10%',
                Cell: ({ cell: { row } }) => {
                    const cellValue = row.original as TSalesData;
                    return (
                        <Box maxW="300px">
                            <Text>{cellValue.name}</Text>
                        </Box>
                    );
                },
            },

            {
                Header: translate('sales_data.column.comment') as string,
                accessor: 'comment',
                sortType: 'caseInsensitive',
                width: '30%',
                Cell: ({ cell: { row } }) => {
                    const cellValue = row.original as TSalesData;
                    return (
                        <Box>
                            <Text>{cellValue.comment}</Text>
                        </Box>
                    );
                },
            },

            {
                Header: translate('sales_data.column.size') as string,
                accessor: 'size',
                sortType: 'caseInsensitive',
                width: '10%',
                Cell: ({ cell: { row } }) => {
                    const cellValue = row.values as TSalesData;
                    const fileSizeKb = (cellValue.size / 1024).toFixed(0);
                    const fileSizeMb = (cellValue.size / 1024 / 1024).toFixed(0);
                    const convertToMb = Number(fileSizeKb) > 1024;
                    return (
                        <Box display="flex">
                            <Text>
                                {convertToMb ? fileSizeMb : fileSizeKb} {convertToMb ? 'MB' : 'KB'}
                            </Text>
                        </Box>
                    );
                },
            },

            {
                Header: translate('sales_data.column.date') as string,
                accessor: 'date',
                sortType: 'caseInsensitive',
                width: '20%',
                Cell: ({ cell: { row } }) => {
                    const cellValue = row.values as TSalesData;
                    const dateStr = cellValue.date;
                    const date = new Date(dateStr);
                    const formattedDate = date.toLocaleDateString('en-GB');
                    return (
                        <Box display="flex">
                            <Text>{formattedDate}</Text>
                        </Box>
                    );
                },
            },

            {
                Header: translate('sales_data.column.user') as string,
                accessor: 'user',
                sortType: 'caseInsensitive',
                width: '20%',
                Cell: ({ cell: { row } }) => {
                    const cellValue = row.values as TSalesData;
                    return (
                        <Box display="flex">
                            <Text>{cellValue.user}</Text>
                        </Box>
                    );
                },
            },
            {
                Header: translate('sales_data.column.status') as string,
                accessor: 'status',
                sortType: 'caseInsensitive',
                width: '20%',
                Cell: ({ cell: { row } }) => {
                    const cellValue = row.original as TSalesData;
                    const tooltipProps: {
                        label?: string;
                    } = {};
                    if (cellValue.status === 'error') {
                        tooltipProps.label = cellValue.error_reason;
                    }

                    return (
                        <Tooltip {...tooltipProps}>
                            <Box display="flex" cursor="default">
                                <Text>{translate(`sales_data.status.${cellValue.status}`)}</Text>
                            </Box>
                        </Tooltip>
                    );
                },
            },
            {
                Header: translate(`sales_data.column.actions`) as string,
                accessor: 'actions',
                width: '10%',
                Cell: ({ cell: { row } }) => {
                    const cellValue = row.original as TSalesData;
                    const DownloadIcon = icons.download;
                    const isDownloading = filesDownloading.includes(cellValue._id);
                    return (
                        <Flex>
                            <Menu size="xs">
                                <MenuButton
                                    size="xs"
                                    as={Button}
                                    rightIcon={isDownloading ? <Spinner size="sm" /> : <ChevronDownIcon />}
                                    isDisabled={isDownloading}
                                >
                                    {translate(`sales_data.select.actions`)}
                                </MenuButton>
                                <MenuList>
                                    <MenuItem
                                        onClick={async () => {
                                            setFilesDownloading((prev) => {
                                                if (!prev.includes(cellValue._id)) return [...prev, cellValue._id];
                                                return prev;
                                            });

                                            const res = await downloadFile({
                                                path: `${cellValue.company_id}/${cellValue.hash}`,
                                            });
                                            const binary = Buffer.from(res.data, 'binary');
                                            const blob = new Blob([binary]);

                                            FileSaver.saveAs(blob, cellValue.name);

                                            setFilesDownloading((prev) => {
                                                return prev.filter((id) => id !== cellValue._id);
                                            });
                                        }}
                                        icon={<DownloadIcon />}
                                    >
                                        {translate(`sales_data.action.download`)}
                                    </MenuItem>
                                </MenuList>
                            </Menu>
                        </Flex>
                    );
                },
            },
        ];

        const filteredColumns = tableColumns.filter((e): e is Column => e !== null);
        return filteredColumns;
    }, [salesData, languages, selectedRows, filesDownloading]);

    const tableInstance = useTable(
        { columns, data, defaultColumn, sortTypes, initialState: { filters } },
        useFilters,
        useGlobalFilter,
        useSortBy,
        usePagination,
    );

    useEffect(() => {
        setFilters(tableInstance.state.filters);
    }, [tableInstance.state.filters]);

    const props = useMemo(
        () => ({
            languages,
            filters,
            retailers,
            isRetailersLoading,
            isRetailerError,
            isSalesDataLoading,
            columns,
            tableInstance,
            companyInfo,
            defaultColumn,
            data,
            sortTypes,
            setLanguages,
            caseInsensitiveSort,
            selectedRows,
        }),
        [
            languages,
            filters,
            retailers,
            isRetailersLoading,
            isRetailerError,
            isSalesDataLoading,
            isSalesDataError,
            columns,
            tableInstance,
            companyInfo,
            defaultColumn,
            data,
            sortTypes,
            selectedRows,
        ],
    );

    return props;
};

export type TColorsHooks = ReturnType<typeof useSalesData>;

export default useSalesData;
